import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { BellRing, Plus, PlayCircle, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const emptyRule = {
    name: "", description: "", is_active: true, priority: 100,
    logic: "AND",
    conditions: [{ field: "sender.status", operator: "==", value: "Nuovo" }],
    actions: [{ type: "add_risk_points", target: "operational", value: 20 }],
};

const ACTION_TYPES = ["add_risk_points", "set_category", "assign_department", "require_manual_review",
    "create_alert", "block_auto_close", "custom_recommendation"];

export default function RulesPage() {
    const { companyId, canAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [schema, setSchema] = useState({ fields: [], operators: [], actions: [], logics: ["AND", "OR"] });
    const [showBuilder, setShowBuilder] = useState(false);
    const [showDryRun, setShowDryRun] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [rule, setRule] = useState(emptyRule);
    const [pecs, setPecs] = useState([]);
    const [selectedPec, setSelectedPec] = useState("");
    const [dryResult, setDryResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: r }, { data: s }] = await Promise.all([
                api.get("/rules"),
                api.get("/rules/schema"),
            ]);
            setItems(r?.items || []);
            setSchema(s || schema);
        } catch { toast.error("Errore"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

    const toggle = async (r, is_active) => {
        try { await api.patch(`/rules/${r.id}`, { is_active }); toast.success(is_active ? "Regola attivata" : "Regola disattivata"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const openBuilder = (existing) => {
        if (existing) {
            setEditingRule(existing);
            setRule({ ...emptyRule, ...existing });
        } else {
            setEditingRule(null);
            setRule(emptyRule);
        }
        setShowBuilder(true);
    };

    const saveRule = async () => {
        if (!rule.name.trim()) { toast.error("Nome obbligatorio"); return; }
        try {
            if (editingRule) {
                await api.patch(`/rules/${editingRule.id}`, rule);
                toast.success("Regola aggiornata");
            } else {
                await api.post("/rules", rule);
                toast.success("Regola creata");
            }
            setShowBuilder(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const openDryRun = async (existing) => {
        setEditingRule(existing);
        try {
            const { data } = await api.get("/pec/?limit=20");
            setPecs(data.items || []);
        } catch { setPecs([]); }
        setSelectedPec("");
        setDryResult(null);
        setShowDryRun(true);
    };

    const runDry = async () => {
        if (!selectedPec) { toast.error("Seleziona una PEC"); return; }
        try {
            const { data } = await api.post("/rules/dry-run", {
                rule_id: editingRule?.id,
                pec_id: selectedPec,
            });
            setDryResult(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const setCondition = (i, patch) => {
        const c = [...rule.conditions];
        c[i] = { ...c[i], ...patch };
        setRule({ ...rule, conditions: c });
    };
    const addCondition = () => setRule({ ...rule, conditions: [...rule.conditions, { field: schema.fields[0], operator: "==", value: "" }] });
    const removeCondition = (i) => setRule({ ...rule, conditions: rule.conditions.filter((_, x) => x !== i) });

    const setAction = (i, patch) => {
        const c = [...rule.actions]; c[i] = { ...c[i], ...patch }; setRule({ ...rule, actions: c });
    };
    const addAction = () => setRule({ ...rule, actions: [...rule.actions, { type: "add_risk_points", target: "operational", value: 10 }] });
    const removeAction = (i) => setRule({ ...rule, actions: rule.actions.filter((_, x) => x !== i) });

    const columns = [
        { key: "name", header: "Regola", sortable: true, render: r => (
            <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center shrink-0"><BellRing className="w-4 h-4 text-[#F59E0B]" /></div>
                <div><div className="text-sm font-medium text-[#172033]">{r.name}</div><div className="text-xs text-[#64748B] max-w-md">{r.description}</div></div>
            </div>
        )},
        { key: "priority", header: "Priorità", sortable: true, render: r => <span className="text-xs font-mono">{r.priority}</span> },
        { key: "executions", header: "Esecuzioni", render: r => (
            <div className="text-xs">
                <div>{r.executions_matched || 0} / {r.executions_total || 0}</div>
                {r.last_execution_at && <div className="text-[10px] text-[#64748B]">{r.last_execution_at.slice(0,10)}</div>}
            </div>
        )},
        { key: "is_active", header: "Attiva", render: r => (
            <Switch checked={!!r.is_active} onCheckedChange={(v) => toggle(r, v)} disabled={!canAdmin()} data-testid={`toggle-rule-${r.id}`} />
        )},
        { key: "act", header: "Azioni", render: r => (
            <div className="flex items-center gap-2">
                <button data-testid={`rule-edit-${r.id}`} onClick={() => openBuilder(r)} className="text-xs text-[#2563EB] hover:underline">Modifica</button>
                <button data-testid={`rule-dryrun-${r.id}`} onClick={() => openDryRun(r)} className="text-xs text-[#16A34A] hover:underline flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5" />Testa</button>
            </div>
        )},
    ];

    return (
        <div className="space-y-6">
            <PageHeader title="Regole e alert" subtitle="Regole deterministiche applicate alla pipeline di analisi." breadcrumbs={[{ label: "Regole e alert" }]}
                actions={canAdmin() ? (
                    <button data-testid="rule-new" onClick={() => openBuilder(null)}
                        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#2563EB] text-white">
                        <Plus className="w-4 h-4" /> Nuova regola
                    </button>
                ) : null}
            />
            <CompanyPecBanner />
            {loading ? <LoadingState /> : <DataTable testId="rules-table" columns={columns} data={items} pageSize={10} getRowKey={r => r.id} emptyTitle="Nessuna regola configurata" />}

            {showBuilder && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBuilder(false)}>
                    <div className="bg-white rounded-xl w-full max-w-3xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">{editingRule ? "Modifica regola" : "Nuova regola"}</h3>
                            <button onClick={() => setShowBuilder(false)} className="text-[#64748B]">✕</button>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label className="text-xs text-[#64748B]">Nome</label>
                                    <input data-testid="rule-form-name" value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })}
                                        className="w-full text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                                </div>
                                <div>
                                    <label className="text-xs text-[#64748B]">Priorità</label>
                                    <input data-testid="rule-form-priority" type="number" value={rule.priority} onChange={(e) => setRule({ ...rule, priority: Number(e.target.value) })}
                                        className="w-full text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-[#64748B]">Descrizione</label>
                                <textarea data-testid="rule-form-desc" rows={2} value={rule.description} onChange={(e) => setRule({ ...rule, description: e.target.value })}
                                    className="w-full text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-xs text-[#64748B]">Logica</label>
                                <select data-testid="rule-form-logic" value={rule.logic} onChange={(e) => setRule({ ...rule, logic: e.target.value })}
                                    className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5">
                                    {(schema.logics || ["AND", "OR"]).map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                                <label className="text-xs text-[#64748B] ml-4">
                                    <input type="checkbox" checked={rule.is_active}
                                        onChange={(e) => setRule({ ...rule, is_active: e.target.checked })} /> Attiva
                                </label>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold">Condizioni</div>
                                    <button data-testid="rule-add-condition" onClick={addCondition} className="text-xs text-[#2563EB]">+ aggiungi</button>
                                </div>
                                {rule.conditions.map((c, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                                        <select data-testid={`rule-cond-field-${i}`} value={c.field} onChange={(e) => setCondition(i, { field: e.target.value })}
                                            className="col-span-5 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5">
                                            {(schema.fields || []).map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                        <select data-testid={`rule-cond-op-${i}`} value={c.operator} onChange={(e) => setCondition(i, { operator: e.target.value })}
                                            className="col-span-3 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5">
                                            {(schema.operators || []).map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        <input data-testid={`rule-cond-val-${i}`} value={c.value ?? ""} onChange={(e) => {
                                            const v = e.target.value;
                                            const parsed = v === "true" ? true : v === "false" ? false : (!isNaN(Number(v)) && v.trim() !== "" ? Number(v) : v);
                                            setCondition(i, { value: parsed });
                                        }} className="col-span-3 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                                        <button onClick={() => removeCondition(i)} className="col-span-1 text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold">Azioni</div>
                                    <button data-testid="rule-add-action" onClick={addAction} className="text-xs text-[#2563EB]">+ aggiungi</button>
                                </div>
                                {rule.actions.map((a, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                                        <select data-testid={`rule-act-type-${i}`} value={a.type} onChange={(e) => setAction(i, { type: e.target.value })}
                                            className="col-span-4 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5">
                                            {ACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <input data-testid={`rule-act-target-${i}`} placeholder="target" value={a.target ?? ""}
                                            onChange={(e) => setAction(i, { target: e.target.value })}
                                            className="col-span-3 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                                        <input data-testid={`rule-act-value-${i}`} placeholder="value" value={a.value ?? ""}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const parsed = v === "true" ? true : v === "false" ? false : (!isNaN(Number(v)) && v.trim() !== "" ? Number(v) : v);
                                                setAction(i, { value: parsed });
                                            }} className="col-span-4 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                                        <button onClick={() => removeAction(i)} className="col-span-1 text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-end gap-2">
                                <button onClick={() => setShowBuilder(false)} className="text-xs px-3 py-1.5 rounded-md border border-[#DCE3EE]">Annulla</button>
                                <button data-testid="rule-save" onClick={saveRule} className="text-xs px-3 py-1.5 rounded-md bg-[#2563EB] text-white">Salva</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDryRun && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowDryRun(false)}>
                    <div className="bg-white rounded-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">Test regola (dry-run)</h3>
                            <button onClick={() => setShowDryRun(false)} className="text-[#64748B]">✕</button>
                        </div>
                        <div className="space-y-3">
                            <select data-testid="dryrun-pec-select" value={selectedPec} onChange={(e) => setSelectedPec(e.target.value)}
                                className="w-full text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5">
                                <option value="">Seleziona una PEC…</option>
                                {pecs.map(p => <option key={p.id} value={p.id}>{p.subject?.slice(0, 60)} — {p.from_address}</option>)}
                            </select>
                            <button data-testid="dryrun-run" onClick={runDry} className="text-xs px-3 py-1.5 rounded-md bg-[#16A34A] text-white">Esegui</button>
                            {dryResult && (
                                <div className="bg-[#F5F7FB] rounded-lg p-3 text-xs" data-testid="dryrun-result">
                                    <div className="font-semibold mb-1">
                                        Risultato: {dryResult.matched
                                            ? <span className="text-[#16A34A]">MATCHED — la regola scatterebbe</span>
                                            : <span className="text-[#DC2626]">NO MATCH</span>}
                                    </div>
                                    {!dryResult.matched && (
                                        <div>
                                            <div className="font-medium mt-2">Condizioni non superate:</div>
                                            <ul className="list-disc ml-4">
                                                {(dryResult.failed_conditions || []).map((f, i) => (
                                                    <li key={i}>{f.field} {f.operator} {String(f.expected)} (attuale: {String(f.actual)})</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {dryResult.matched && dryResult.would_execute_actions?.length > 0 && (
                                        <div>
                                            <div className="font-medium mt-2">Azioni che scatterebbero:</div>
                                            <ul className="list-disc ml-4">
                                                {dryResult.would_execute_actions.map((a, i) => (
                                                    <li key={i}>{a.type} → {a.target || "-"}: {String(a.value ?? "")}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="mt-2 text-[10px] text-[#64748B]">Dry-run: nessun dato è stato modificato.</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
